import { type Config } from 'tailwindcss'
import defaultTheme from 'tailwindcss/defaultTheme.js'

export const extendedTheme = {
	screens: {
		lg: '770px',
		macbook: '1700px',
		'2xl': '1800px',
	},
	colors: {
		sidebar: {
			DEFAULT: 'hsl(var(--sidebar-background))',
			foreground: 'hsl(var(--sidebar-foreground))',
			primary: 'hsl(var(--sidebar-primary))',
			'primary-foreground': 'hsl(var(--sidebar-primary-foreground))',
			accent: 'hsl(var(--sidebar-accent))',
			'accent-foreground': 'hsl(var(--sidebar-accent-foreground))',
			border: 'hsl(var(--sidebar-border))',
			ring: 'hsl(var(--sidebar-ring))',
		},
		border: 'hsl(var(--border))',
		input: {
			DEFAULT: 'hsl(var(--input))',
			invalid: 'hsl(var(--input-invalid))',
		},
		ring: {
			DEFAULT: 'hsl(var(--ring))',
			invalid: 'hsl(var(--destructive))',
		},
		background: 'hsl(var(--background))',
		foreground: {
			DEFAULT: 'hsl(var(--foreground))',
			destructive: 'hsl(var(--destructive))',
		},
		primary: {
			DEFAULT: 'hsl(var(--brand-dusk))',
			foreground: 'hsl(var(--primary-foreground))',
		},
		secondary: {
			DEFAULT: 'hsl(var(--brand-aquamarine))',
			foreground: 'hsl(var(--secondary-foreground))',
		},
		destructive: {
			DEFAULT: 'hsl(var(--destructive))',
			foreground: 'hsl(var(--destructive-foreground))',
		},
		muted: {
			DEFAULT: 'hsl(var(--muted))',
			foreground: 'hsl(var(--muted-foreground))',
		},
		accent: {
			DEFAULT: 'hsl(var(--accent))',
			foreground: 'hsl(var(--accent-foreground))',
		},
		popover: {
			DEFAULT: 'hsl(var(--popover))',
			foreground: 'hsl(var(--popover-foreground))',
		},
		status: {
			success: 'hsl(var(--success))',
			warning: 'hsl(var(--warning))',
			danger: 'hsl(var(--danger))',
			info: 'hsl(var(--info))',
		},
		brand: {
			aquamarine: {
				DEFAULT: 'hsl(var(--brand-aquamarine))',
				foreground: 'hsl(var(--foreground))',
			},
			dusk: {
				DEFAULT: 'hsl(var(--brand-dusk))',
				foreground: 'hsl(var(--background))',
			},
			mist: {
				DEFAULT: 'hsl(var(--brand-mist))',
				foreground: 'hsl(var(--foreground))',
			},
			sand: {
				DEFAULT: 'hsl(var(--brand-sand))',
				foreground: 'hsl(var(--foreground))',
			},
			earth: {
				DEFAULT: 'hsl(var(--brand-earth))',
				foreground: 'hsl(var(--background))',
			},
		},
		card: {
			DEFAULT: 'hsl(var(--card))',
			foreground: 'hsl(var(--card-foreground))',
		},
	},
	borderColor: {
		DEFAULT: 'hsl(var(--border))',
	},
	borderRadius: {
		lg: 'var(--radius)',
		md: 'calc(var(--radius) - 2px)',
		sm: 'calc(var(--radius) - 4px)',
		xxs: 'calc(var(--radius) - 6px)',
	},
	fontFamily: {
		sans: ['var(--font-sans)', ...defaultTheme.fontFamily.sans],
		serif: ['var(--font-serif)', ...defaultTheme.fontFamily.serif],
		'serif-book': ['var(--font-serif-book)', ...defaultTheme.fontFamily.serif],
	},
	gridTemplateColumns: {
		layout: 'var(--navigation-width) 1fr',
	},
	gridTemplateRows: {
		'layout-mobile':
			'80px minmax(calc(100vh - 80px - 50px), auto) 50px minmax(0, 1fr) minmax(400px, auto)',
		'layout-tablet':
			'100px minmax(calc(100vh - 100px - 60px), auto) 60px minmax(0, 1fr) minmax(400px, auto)',
		layout:
			'120px minmax(calc(100vh - 120px - 80px), auto) 80px minmax(0, 1fr) minmax(400px, auto)',
	},
	width: {
		navigation: 'var(--navigation-width)',
	},
	boxShadow: {
		'low-elevation': 'var(--shadow-elevation-low)',
		'mid-elevation': 'var(--shadow-elevation-medium)',
		'high-elevation': 'var(--shadow-elevation-high)',
	},
	height: {
		header: 'var(--header-height)',
		'header-tablet': 'var(--header-height-tablet)',
		'header-mobile': 'var(--header-height-mobile)',
	},
	fontSize: {
		// 1rem = 16px
		/** 120px size / 112px high / bold */
		huge: [
			'7.5rem',
			{
				lineHeight: '7rem',
				fontWeight: '700',
			},
		],
		/** 96px size / 100px high / bold */
		giga: ['6rem', { lineHeight: '6.25rem', fontWeight: '700' }],
		/** 80px size / 84px high / bold */
		mega: ['5rem', { lineHeight: '5.25rem', fontWeight: '700' }],
		/** 56px size / 62px high / bold */
		h1: ['3.5rem', { lineHeight: '3.875rem', fontWeight: '700' }],
		/** 40px size / 48px high / bold */
		h2: ['2.5rem', { lineHeight: '3rem', fontWeight: '700' }],
		/** 32px size / 36px high / bold */
		h3: ['2rem', { lineHeight: '2.25rem', fontWeight: '700' }],
		/** 28px size / 36px high / bold */
		h4: ['1.75rem', { lineHeight: '2.25rem', fontWeight: '700' }],
		/** 24px size / 32px high / bold */
		h5: ['1.5rem', { lineHeight: '2rem', fontWeight: '700' }],
		/** 16px size / 20px high / bold */
		h6: ['1rem', { lineHeight: '1.25rem', fontWeight: '700' }],

		/** 32px size / 36px high / normal */
		'body-2xl': ['2rem', { lineHeight: '2.25rem' }],
		/** 28px size / 36px high / normal */
		'body-xl': ['1.75rem', { lineHeight: '2.25rem' }],
		/** 24px size / 32px high / normal */
		'body-lg': ['1.5rem', { lineHeight: '2rem' }],
		/** 20px size / 28px high / normal */
		'body-md': ['1.25rem', { lineHeight: '1.75rem' }],
		/** 16px size / 20px high / normal */
		'body-sm': ['1rem', { lineHeight: '1.25rem' }],
		/** 14px size / 18px high / normal */
		'body-xs': ['0.875rem', { lineHeight: '1.125rem' }],
		/** 12px size / 16px high / normal */
		'body-2xs': ['0.75rem', { lineHeight: '1rem' }],

		/** 18px size / 24px high / semibold */
		caption: ['1.125rem', { lineHeight: '1.5rem', fontWeight: '600' }],
		/** 12px size / 16px high / bold */
		button: ['0.75rem', { lineHeight: '1rem', fontWeight: '700' }],
	},
	spacing: {
		xxxs: '4px',
		xxs: '8px',
		xs: '12px',
		sm: '16px',
		md: '24px',
		lg: '32px',
		xl: '48px',
		xxl: '64px',
		xxxl: '96px',
		navigation: 'var(--navigation-width)',
	},
	keyframes: {
		'caret-blink': {
			'0%,70%,100%': { opacity: '1' },
			'20%,50%': { opacity: '0' },
		},
		'accordion-down': {
			from: { height: '0' },
			to: { height: 'var(--radix-accordion-content-height)' },
		},
		'accordion-up': {
			from: { height: 'var(--radix-accordion-content-height)' },
			to: { height: '0' },
		},
		'enter-opacity': {
			from: { opacity: '0' },
			to: { opacity: '1' },
		},
	},
	animation: {
		'caret-blink': 'caret-blink 1.25s ease-out infinite',
		'accordion-down': 'accordion-down 0.2s ease-out',
		'accordion-up': 'accordion-up 0.2s ease-out',
		'enter-opacity': 'enter-opacity 1.5s ease-out',
	},
} satisfies Config['theme']
